/**
 * InterventionEngineCard — Phase D Section 10.
 *
 * Deep-links the user from DHI into the specialised Nutramilo labs:
 *   - Nutrition Lab (Plate Simulator + Meal Planner)
 *   - Mind & Body Lab (Stress / Sleep / Mindfulness)
 *   - Physical Activity Lab
 *   - Supplement Intelligence (with evidence grades A/B/C)
 *
 * Recommendations are derived from the snapshot — we surface ONE clear
 * action per lab based on the user's weakest oral-health environment.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Utensils, Brain, Activity, Sparkles, ArrowRight } from "lucide-react";

const EVIDENCE_PILL = {
  A: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  B: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  C: "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300",
};

function pickRecommendations(snapshot, lang) {
  const bg = lang === "bg";
  const eco = snapshot.oral_ecosystem || {};
  // iter1111 — When `oral_metabolic` is retired its `components` is empty,
  // so the old `(met.sugar?.score || 100) < 60` fallback silently resolved
  // to the "healthy" branch for every user. That is a false-negative: we
  // showed "no action needed" to users for whom we actually had no scored
  // data. Use the backend's retirement status to detect this and short-
  // circuit the metabolic-driven branches to the educational default.
  const met = snapshot.oral_metabolic?.components || {};
  const metabolicRetired = !!(snapshot.oral_metabolic?._retired
    || snapshot.oral_metabolic?.status === "retired_unvalidated");
  const metSugar = metabolicRetired ? null : met.sugar?.score;
  const metProtective = metabolicRetired ? null : met.protective?.score;
  const cariesBand = snapshot.caries_intelligence?.band;
  const perioBand = snapshot.periodontal_intelligence?.band;
  const signals = snapshot.signals || {};

  const recs = [];

  // Nutrition Lab
  let nutritionMsg, nutritionRoute;
  if (metSugar !== null && metSugar < 60) {
    nutritionMsg = bg ? "Намалете добавени захари до < 25g/ден" : "Reduce added sugar < 25g/day";
  } else if (metProtective !== null && metProtective < 60) {
    nutritionMsg = bg ? "Увеличете фибри до ≥ 30g/ден (полифеноли + ферментируеми)" : "Increase fiber ≥ 30g/day (polyphenols + fermentable)";
  } else if (metabolicRetired) {
    // iter1111 — honest "we don't have scored metabolic data" message
    // instead of pretending the diet is already optimal.
    nutritionMsg = bg ? "Добавете няколко ястия за ден, за да получите конкретна препоръка" : "Log a few meals to receive a tailored recommendation";
  } else {
    nutritionMsg = bg ? "Поддържайте средиземноморски модел и DIL < 90" : "Maintain Mediterranean pattern with DIL < 90";
  }
  nutritionRoute = "/dashboard/diary";
  recs.push({
    key: "nutrition", icon: Utensils, color: "#F97316",
    label_bg: "Nutrition Lab", label_en: "Nutrition Lab",
    message: nutritionMsg,
    route: nutritionRoute,
    cta: bg ? "Отвори Plate Simulator" : "Open Plate Simulator",
  });

  // Mind & Body
  let mindMsg;
  if (signals.smoking) {
    mindMsg = bg ? "Откажете цигарите — № 1 модифицируем риск (EFP)" : "Quit smoking — #1 modifiable risk (EFP)";
  } else if ((signals.hs_crp || 0) > 3) {
    mindMsg = bg ? "Стрес-регулация + сън ≥ 7 ч/нощ → намалява CRP" : "Stress regulation + ≥ 7h sleep → reduces CRP";
  } else {
    mindMsg = bg ? "10 мин дишане 4-7-8 преди сън — поддържа резолюция" : "10 min 4-7-8 breathing before sleep — maintains resolution";
  }
  recs.push({
    key: "mind", icon: Brain, color: "#A855F7",
    label_bg: "Mind & Body Lab", label_en: "Mind & Body Lab",
    message: mindMsg,
    route: "/dashboard/mindbody-lab",
    cta: bg ? "Към Mind & Body Lab" : "Go to Mind & Body Lab",
  });

  // Activity Lab
  let actMsg;
  if ((signals.hs_crp || 0) > 3 || perioBand === "orange" || perioBand === "red") {
    actMsg = bg ? "150 мин/седм. умерено аеробно → антиинфламаторно" : "150 min/wk moderate aerobic → anti-inflammatory";
  } else if (cariesBand === "red" || cariesBand === "orange") {
    actMsg = bg ? "Бързо ходене 30 мин/ден — стимулира слюнообразуване" : "Brisk 30 min walks — stimulates saliva flow";
  } else {
    actMsg = bg ? "Силови 2×/седм. + аеробно 3×/седм." : "Strength 2×/wk + aerobic 3×/wk";
  }
  recs.push({
    key: "activity", icon: Activity, color: "#22C55E",
    label_bg: "Physical Activity Lab", label_en: "Physical Activity Lab",
    message: actMsg,
    route: "/dashboard/mindbody-lab",
    cta: bg ? "Към Activity Lab" : "Go to Activity Lab",
  });

  // Supplements (evidence-grade)
  const supplements = [];
  if ((signals.vitamin_d || 0) < 40) {
    supplements.push({ name: bg ? "Витамин D3" : "Vitamin D3", dose: "1000-2000 IU/ден", evidence: "A" });
  }
  if ((signals.omega_3_index || 0) < 8) {
    supplements.push({ name: bg ? "Омега-3 EPA+DHA" : "Omega-3 EPA+DHA", dose: "1-2 g/ден", evidence: "A" });
  }
  supplements.push({ name: bg ? "Ксилитол (дъвки)" : "Xylitol gum", dose: "5-10 g/ден след хранене", evidence: "A" });
  if ((signals.fiber_per_day || 0) < 25) {
    supplements.push({ name: bg ? "Полифеноли (зелен чай)" : "Polyphenols (green tea)", dose: "2-3 чаши/ден", evidence: "B" });
  }
  supplements.push({ name: bg ? "Пробиотик (L. reuteri)" : "Probiotic (L. reuteri)", dose: "1 капс/ден", evidence: "B" });
  recs.push({
    key: "supplements", icon: Sparkles, color: "#0EA5E9",
    label_bg: "Supplement Intelligence", label_en: "Supplement Intelligence",
    supplements: supplements.slice(0, 4),
    route: "/dashboard/nutrition-lab",
    cta: bg ? "Към Nutrition Lab" : "Go to Nutrition Lab",
  });

  return recs;
}

export default function InterventionEngineCard({ snapshot, isDark, lang = "bg", cardCls }) {
  const bg = lang === "bg";
  const recs = pickRecommendations(snapshot, lang);
  const t = bg
    ? {
        title: "Персонализирани препоръки — нежни лостове",
        subtitle: "Едно действие на лаборатория, изведено от текущите ви оценки.",
      }
    : {
        title: "Personalised recommendations — gentle levers",
        subtitle: "One action per lab, derived from your current scores.",
      };

  return (
    <div className={`${cardCls} p-5`} data-testid="dhi-intervention-engine-card">
      <h3 className="text-lg font-bold mb-1">{t.title}</h3>
      <p className="text-xs opacity-60 mb-4">{t.subtitle}</p>

      <div className="grid md:grid-cols-2 gap-3">
        {recs.map((r) => {
          const Icon = r.icon;
          return (
            <Link
              key={r.key}
              to={r.route}
              className={`relative p-3 rounded-xl border transition-all hover:scale-[1.01] hover:shadow-md flex flex-col gap-2 ${
                isDark ? "border-white/10 bg-white/[0.02] hover:bg-white/5" : "border-gray-200 bg-gray-50 hover:bg-white"
              }`}
              data-testid={`dhi-intervention-${r.key}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${r.color}22`, color: r.color }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="text-sm font-bold flex-1">{bg ? r.label_bg : r.label_en}</div>
                <ArrowRight className="w-3.5 h-3.5 opacity-50" />
              </div>
              {r.message && (
                <div className="text-xs opacity-80 leading-snug">{r.message}</div>
              )}
              {r.supplements && (
                <ul className="space-y-1 text-[11px]">
                  {r.supplements.map((s, i) => (
                    <li key={i} className="flex items-center gap-1.5">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${EVIDENCE_PILL[s.evidence]}`}>{s.evidence}</span>
                      <span className="flex-1"><strong>{s.name}</strong> · <span className="opacity-60">{s.dose}</span></span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="text-[10px] font-semibold opacity-70 mt-auto" style={{ color: r.color }}>
                {r.cta} →
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
